import { demoFunc } from './dees-harness-sidebar.demo.js';
import {
  DeesElement,
  html,
  css,
  customElement,
  type TemplateResult,
  property,
  state,
  cssManager,
  directives,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import { harnessSidebarStyles } from '../harness.styles.js';
import '../../00group-utility/dees-icon/dees-icon.js';

const { repeat } = directives;

/** Every section body arrives through `slot="section-<key>"`. */
const sectionSlotPrefix = 'section-';
const sectionSlotName = (keyArg: string): string => `${sectionSlotPrefix}${keyArg}`;

const sameKeys = (leftArg: ReadonlySet<string>, rightArg: ReadonlySet<string>): boolean =>
  leftArg.size === rightArg.size && [...leftArg].every((keyArg) => rightArg.has(keyArg));

declare global {
  interface HTMLElementTagNameMap {
    'dees-harness-sidebar': DeesHarnessSidebar;
  }
}

/**
 * One collapsible section of a sidebar panel. The element renders the heading
 * row; the body is the host's own content, slotted as `slot="section-<key>"`.
 */
export interface IHarnessSidebarSection {
  /** Stable identity, unique within `sections`; also names the content slot. */
  key: string;
  label: string;
  /** Icon shown before the label, e.g. `lucide:Gauge`. */
  icon?: string;
  /** Right-aligned summary such as a count, a rate, or a live value. */
  badge?: string;
}

/**
 * Detail of `harness-sidebar-section-change`: the user opened another section
 * or collapsed the open one.
 */
export interface IHarnessSidebarSectionChangeDetail {
  /** Key of the now-open section, or `undefined` when nothing is open. */
  expandedSection: string | undefined;
}

/**
 * Sidebar shell: the panel chrome of `dees-harness-session-sidebar` with
 * host-declared sections instead of session-specific ones. It owns the
 * single-open accordion and nothing else — every section body is slotted
 * content, so the host keeps its own state, updates, and presentation. A
 * section whose slot holds no content is not offered at all, and the host
 * mounts and unmounts the element to show or hide the panel.
 */
@customElement('dees-harness-sidebar')
export class DeesHarnessSidebar extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Agent Chat'];

  /** Sections in render order; each one's body is slotted as `section-<key>`. */
  @property({ attribute: false })
  accessor sections: IHarnessSidebarSection[] = [];

  /**
   * Key of the open section. Host-settable and kept in sync with what the user
   * opens; it defaults to the first section that has content, and `undefined`
   * leaves the panel collapsed once a section has been opened. A declared
   * section without content renders collapsed and opens as soon as its content
   * arrives, so the key survives an asynchronous body.
   */
  @property()
  accessor expandedSection: string | undefined = undefined;

  /** Freezes the accordion: headings stay visible but cannot be toggled. */
  @property({ type: Boolean })
  accessor disabled: boolean = false;

  @state()
  private accessor populatedSections: ReadonlySet<string> = new Set<string>();

  private sectionStateInitialized = false;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    harnessSidebarStyles,
    css`
      :host {
        background: var(--dees-color-bg-canvas);
      }

      /* A section without content is not offered at all: the hidden attribute
         alone would still be laid out, because the shared chrome's display
         declaration wins over the user-agent rule. The divider then follows the
         last visible section instead of the last declared one. */
      .section[hidden] {
        display: none;
      }

      .section:not(:has(~ .section:not([hidden]))) {
        border-bottom: 0;
      }

      /* Section bodies stay as compact as the session panel's; slotted content
         inherits this and can override it per element. */
      .sectionContent {
        font-size: var(--dees-font-caption1-size, 11px);
      }

      .sectionHeading:disabled {
        opacity: 0.62;
        cursor: default;
      }

      .sectionHeading:disabled:hover {
        background: var(--dees-color-bg-canvas);
      }
    `,
  ];

  protected willUpdate(changedPropertiesArg: Map<PropertyKey, unknown>): void {
    if (changedPropertiesArg.has('expandedSection')) this.sectionStateInitialized = true;
    const populated = this.populatedSectionsInOrder;
    // Slot assignment is only known after the first render, so an empty panel
    // never overrides a host-set section.
    if (populated.length === 0) return;
    if (!this.sectionStateInitialized) {
      this.expandedSection = populated[0].key;
      this.sectionStateInitialized = true;
      return;
    }
    const open = this.expandedSection;
    // A section the host withdrew from `sections` can never open again, so the
    // panel moves on. A declared section whose content is momentarily missing
    // keeps the key and renders collapsed until the content is back, so an
    // asynchronous body never costs the host or the user their choice.
    if (open !== undefined && !this.sections.some((section) => section.key === open)) {
      this.expandedSection = populated[0].key;
    }
  }

  public render(): TemplateResult {
    return html`
      <div class="sidebar">
        ${repeat(
          this.sections,
          (section) => section.key,
          (section) => this.renderSection(section),
        )}
      </div>
    `;
  }

  /** Declared sections that currently have slotted content, in render order. */
  private get populatedSectionsInOrder(): IHarnessSidebarSection[] {
    return this.sections.filter((section) => this.populatedSections.has(section.key));
  }

  /**
   * The heading row is the element's own; the body is the host's, and it stays
   * mounted while collapsed so it keeps its state and scroll position.
   */
  private renderSection(sectionArg: IHarnessSidebarSection): TemplateResult {
    const populated = this.populatedSections.has(sectionArg.key);
    const expanded = populated && this.expandedSection === sectionArg.key;
    const contentId = `harness-sidebar-section-${sectionArg.key}`;
    return html`
      <section
        class=${`section ${expanded ? 'expanded' : ''}`}
        data-section=${sectionArg.key}
        ?hidden=${!populated}
      >
        ${populated ? html`
          <button
            class="sectionHeading"
            type="button"
            aria-expanded=${String(expanded)}
            aria-controls=${contentId}
            ?disabled=${this.disabled}
            @click=${() => this.toggleSection(sectionArg.key)}
          >
            <dees-icon
              icon=${expanded ? 'lucide:ChevronDown' : 'lucide:ChevronRight'}
              iconSize="13"
            ></dees-icon>
            ${sectionArg.icon ? html`<dees-icon icon=${sectionArg.icon} iconSize="13"></dees-icon>` : ''}
            <span class="sectionHeadingText">${sectionArg.label}</span>
            ${sectionArg.badge ? html`<span class="sectionSummary">${sectionArg.badge}</span>` : ''}
          </button>
        ` : ''}
        <div
          id=${contentId}
          class="sectionContent"
          role="region"
          aria-label=${sectionArg.label}
          aria-hidden=${String(!expanded)}
          ?hidden=${!expanded}
        >
          <slot
            name=${sectionSlotName(sectionArg.key)}
            @slotchange=${this.handleSectionSlotChange}
          ></slot>
        </div>
      </section>
    `;
  }

  /**
   * A section is offered only while its slot holds content, so the panel
   * recomputes from its live slots whenever the host fills or empties one.
   */
  private readonly handleSectionSlotChange = (): void => {
    const populated = new Set<string>();
    const sectionSlots = this.renderRoot.querySelectorAll<HTMLSlotElement>(
      `slot[name^="${sectionSlotPrefix}"]`,
    );
    for (const slot of sectionSlots) {
      if (slot.assignedElements({ flatten: true }).length > 0) {
        populated.add(slot.name.slice(sectionSlotPrefix.length));
      }
    }
    if (sameKeys(this.populatedSections, populated)) return;
    this.populatedSections = populated;
  };

  private toggleSection(keyArg: string): void {
    if (this.disabled) return;
    const expandedSection = this.expandedSection === keyArg ? undefined : keyArg;
    this.sectionStateInitialized = true;
    this.expandedSection = expandedSection;
    const detail: IHarnessSidebarSectionChangeDetail = { expandedSection };
    this.dispatchEvent(new CustomEvent<IHarnessSidebarSectionChangeDetail>(
      'harness-sidebar-section-change',
      { detail, bubbles: true, composed: true },
    ));
  }
}
